import { useEffect, useRef } from 'react';
import { BehaviorSubject } from 'rxjs';

export const createDataModuleStateObservable = () => new BehaviorSubject({});

/**
 * Connect a global Observable to a data module's state observable.
 * This allows using data module state in epics
 *
 * Example:
 * ```
 * // useFooDataModuleContext.js
 *
 * export const fooDataModuleState$ = createDataModuleStateObservable();
 *
 * const useFooDataModule = () => {
 *   const fooDataModule = useDataModule(() => {
 *     // ...
 *   });
 *
 *   useDataModuleStateObservable(fooDataModule, fooDataModuleState$);
 * };
 *
 * export const getFooBar = state => getData(state).bar;
 *
 * // epics/foo.js
 * const foo = (action$) => action$.pipe(
 *   ofType(fooAction().type),
 *   withLatestFrom(fooDataModuleState$),
 *   mergeMap(([action, fooDataModuleState]) => {
 *     const bar = getFooBar(fooDataModuleState);
 *     // ...
 *   })
 * );
 * ```
 * @param {*} dataModule
 * @param {Subject} exposedState$
 */
export const useDataModuleStateObservable = (dataModule, exposedState$) => {
    const subscriptionRef = useRef(null);

    if (subscriptionRef.current === null) {
        subscriptionRef.current = dataModule.state$.subscribe(exposedState$);
    }

    useEffect(() => {
        const subscription = subscriptionRef.current;
        return () => subscription.unsubscribe();
    }, []);
};
